import React from "react";
import { Banner, Button, Card, StatusPill } from "@/ds";
import { cockpit } from "@/api";
import { cachedRead } from "@/api/read-cache";
import { Icon } from "@/lib";
import { useLiveInvalidation } from "@/live/live-provider";
import { useT } from "../i18n";
import {
  NO_SETUP_SUMMARY,
  summariseSetupResponse,
  type SetupSummary,
  type SetupTaskKey,
} from "./setup-tasks";
import styles from "./screen-getting-started.module.css";

/**
 * The first five minutes of a business (#1286).
 *
 * Before this, an invited owner registered, was shown their new address once in a modal, clicked
 * through, and arrived at an empty Inbox — with nothing anywhere explaining what the product does,
 * what it does on its own, or what they were supposed to do first. The in-app demo tour of the
 * time explained the product, but it belonged to the demo business and a paying one never saw it.
 *
 * So: two parts, in this order.
 *
 * **What happens now**, first, because a business's first question about software that answers its
 * customers is not "what do I click" but "what will this send without me". The answer — nothing,
 * ever, without a person approving it — is the single fact worth putting above everything else.
 *
 * **What to do next**, second: the same tasks the reminder strip (#1287) keeps visible, from the
 * same server read, so the two can never contradict each other. Each says whether it is done, and
 * done ones stay on the page rather than vanishing — a list that empties as you work it is a list
 * that stops being re-readable, and this page exists to be re-read.
 *
 * The missing-address warning is not a task and carries no button: only the platform side can
 * provision an address, and a button that cannot help is worse than a sentence that explains.
 */

interface TaskCopy {
  titleKey: string;
  bodyKey: string;
  actionKey: string;
  icon: "pencil" | "users";
}

const TASKS: Record<SetupTaskKey, TaskCopy> = {
  signature: {
    titleKey: "gettingStarted.task.signature.title",
    bodyKey: "gettingStarted.task.signature.body",
    actionKey: "gettingStarted.task.signature.action",
    icon: "pencil",
  },
  colleagues: {
    titleKey: "gettingStarted.task.colleagues.title",
    bodyKey: "gettingStarted.task.colleagues.body",
    actionKey: "gettingStarted.task.colleagues.action",
    icon: "users",
  },
};

/** In the order they are worth doing, so a done one keeps its place rather than jumping. */
const TASK_ORDER: SetupTaskKey[] = ["signature", "colleagues"];

export interface GettingStartedScreenProps {
  /** Opens Settings on its Signature section. */
  onOpenSignature: () => void;
  /** Opens Staff. */
  onOpenStaff: () => void;
}

export function GettingStartedScreen({
  onOpenSignature,
  onOpenStaff,
}: GettingStartedScreenProps) {
  const t = useT();
  // #1536: the answer from the last visit stays on screen while it refreshes.
  const [summary, setSummary] = React.useState<SetupSummary | null>(() => {
    const remembered = cachedRead("getSetupState");
    return remembered === undefined ? null : summariseSetupResponse(remembered);
  });

  /**
   * Nothing known, rather than something wrong.
   *
   * A rejected fetch and a 200 that is not the schema land in the same place: `null`, which renders
   * the explanation and no claim at all about setup state. Saying "signature not set" to a business
   * that set one is a small annoyance; saying "done" to one that did not is a customer receiving a
   * reply with no legal block, which nobody finds out about from here — and an unrecognised body
   * summarises to "nothing outstanding", which is exactly that claim, so it must not reach state.
   */
  const load = React.useCallback(() => {
    let disposed = false;
    cockpit.getSetupState().then(
      (state) => {
        if (!disposed) setSummary(summariseSetupResponse(state));
      },
      () => {
        if (!disposed) setSummary(null);
      },
    );
    return () => {
      disposed = true;
    };
  }, []);

  // Both halves of the answer move on ordinary writes: the signature is a `settings` row, an added
  // colleague is a `users` row. Refetching on either means a task marks itself done as soon as it
  // is done, without the person having to guess that the page is stale.
  const settingsTicks = useLiveInvalidation("settings");
  const staffTicks = useLiveInvalidation("staff");

  // One effect for mount and every tick, so a reply to a superseded request cannot land after a
  // newer one — the tenant stream sends a full first event as it connects, so a second read
  // follows the first on every load.
  React.useEffect(() => load(), [load, settingsTicks, staffTicks]);

  const known = summary ?? NO_SETUP_SUMMARY;
  const outstanding = new Set(known.tasks);
  const action: Record<SetupTaskKey, () => void> = {
    signature: onOpenSignature,
    colleagues: onOpenStaff,
  };

  return (
    // #1431: the Settings → Setup section, under Settings' own heading and management styles.
    <div data-testid="getting-started">
      <p className="management-copy">{t("gettingStarted.sub")}</p>

      <div className={styles.stack}>
        <Card>
          <div className={styles.explainer}>
            <h2 className={styles.sectionTitle}>{t("gettingStarted.how.title")}</h2>
            <ol className={styles.steps}>
              {["receive", "read", "review", "send"].map((step) => (
                <li key={step} className={styles.step}>
                  <span className={styles.stepBody}>
                    <strong>{t(`gettingStarted.how.${step}.title`)}</strong>
                    <span className="subtle">{t(`gettingStarted.how.${step}.body`)}</span>
                  </span>
                </li>
              ))}
            </ol>
            {/* Stated on its own, after the steps, because it is the promise rather than a step:
                there is no configuration that turns it off and no hurry that skips it. */}
            <Banner tone="system" icon={<Icon name="lock" size={18} />}
              title={t("gettingStarted.how.guaranteeTitle")}>
              {t("gettingStarted.how.guaranteeBody")}
            </Banner>
          </div>
        </Card>

        {summary && known.mailWarning && (
          <Banner
            tone="warning"
            icon={<Icon name="alert" size={18} />}
            title={t("gettingStarted.mailWarningTitle")}
            data-testid="getting-started-mail-warning"
          >
            {t("gettingStarted.mailWarningBody")}
          </Banner>
        )}

        <Card>
          <h2 className={styles.sectionTitle}>{t("gettingStarted.next.title")}</h2>
          {!summary ? (
            // The one honest thing to say when the server's answer cannot be trusted.
            <p className={styles.unknown} data-testid="getting-started-unknown">
              {t("gettingStarted.next.unknown")}
            </p>
          ) : (
            <ul className={styles.tasks} data-testid="getting-started-tasks">
              {TASK_ORDER.map((key) => {
                const copy = TASKS[key];
                const done = !outstanding.has(key);
                return (
                  <li key={key} className={styles.task} data-testid={`getting-started-task-${key}`}>
                    <span className={styles.taskIcon} aria-hidden="true">
                      <Icon name={copy.icon} size={18} />
                    </span>
                    <span className={styles.taskBody}>
                      <span className={styles.taskHead}>
                        <strong>{t(copy.titleKey)}</strong>
                        <StatusPill tone={done ? "success" : "warning"}>
                          {t(done ? "gettingStarted.state.done" : "gettingStarted.state.open")}
                        </StatusPill>
                      </span>
                      <span className="subtle">{t(copy.bodyKey)}</span>
                    </span>
                    {/* The action stays after it is done: this page is re-read, and "go and look at
                        the thing you set" is a reasonable thing to want. */}
                    <Button variant={done ? "secondary" : "primary"} size="sm" onClick={action[key]}>
                      {t(copy.actionKey)}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
